// @flow
import axios from 'axios';
import {
  isArray,
  isObject,
  each,
  mapKeys,
  snakeCase,
  camelCase,
  split,
} from 'lodash';
import { deepMapKeys } from '../utils/commonUtils';
import type { Axios } from 'axios';

const DEFAULT_API_CONFIG = {
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
  },
  baseURL: '/api/v1',
  timeout: 300000, // サーバーのタイムアウトは180sなので、それより長くする
  apiVersion: 'v1',
};

export default class AxiosClient {
  client: Axios;

  constructor(config: any = {}) {
    const defaultConfig = DEFAULT_API_CONFIG;
    this.client = axios.create({
      ...this._defaultClientConfig(defaultConfig),
      ...config,
    });
    this._requestInterceptor();

    if (config.skipResponseInterceptor) return;
    this._responseInterceptor();
  }

  buildFormData(
    values: Object,
    { keyMaps = {}, jsonAsArray = false }: Object = {},
  ) {
    const formData = new FormData();
    each(values, (value, key) => {
      const k = keyMaps[key] || snakeCase(key);
      if ((isArray(value) || isObject(value)) && jsonAsArray) {
        formData.append(k, JSON.stringify(value));
      } else if (isArray(value)) {
        value.forEach(v => formData.append(`${k}[]`, v));
      } else if (isObject(value)) {
        Object.keys(value).forEach(key2 => {
          const k2 = keyMaps[key2] || snakeCase(key2);
          formData.append(`${k}[${k2}]`, value[key2]);
        });
      } else {
        formData.append(k, value);
      }
    });
    return formData;
  }

  _requestInterceptor() {
    this.client.interceptors.request.use(config => {
      if (!this._isFormDataRequest(config)) {
        const returnKey = key => (key === '_destroy' ? key : snakeCase(key));
        config.data = deepMapKeys(config.data, returnKey);
      }
      return config;
    });
  }

  _isFormDataRequest(config: Object) {
    return config.data && config.data.toString().includes('FormData');
  }

  _responseInterceptor() {
    this.client.interceptors.response.use(
      res => {
        if (res.data) {
          res.data = deepMapKeys(res.data, key => camelCase(key));
        }
        return res;
      },
      (error: any) => {
        error.response.data = this._camelCaseErrorResponse(error.response.data);
        throw error;
      },
    );
  }

  _camelCaseErrorResponse(error: Object) {
    if (error.details) {
      error.details = mapKeys(error.details, (value, key) =>
        camelCase(split(key, '.').pop()),
      );
    }
    return mapKeys(error, (value, key) => camelCase(key));
  }

  authenticityToken(): string | null {
    const token = document.querySelector('meta[name="csrf-token"]');
    if (token && token instanceof window.HTMLMetaElement) {
      return token.content;
    }
    return null;
  }

  _defaultClientConfig(defaultConfig: Object) {
    defaultConfig.headers['X-CSRF-Token'] = this.authenticityToken();
    return defaultConfig;
  }
}
